/**
 * Phase 45.1b — `/sso/callback` landing page.
 *
 * After the IdP authenticates the user, the API's ACS/OIDC handler
 * redirects the browser here with a single-use exchange code
 * (`?code=…&returnTo=…`) — tokens never ride a redirect URL. This page
 * redeems the code via `completeSso` (POST /auth/sso/exchange) and then
 * navigates to the validated in-app `returnTo` path.
 *
 * The exchange MUST fire exactly once: the code is single-use with a
 * 60s TTL, and React 18 StrictMode double-mounts effects in dev — the
 * module-level guard below (a ref survives a re-render but NOT a
 * remount) keeps the second mount from burning the code and stranding
 * the user on a "code already used" error.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Alert, Logo, Spinner } from '@heroui/react';
import { useAuthStore } from '../../stores/authStore';
import { useUiStore } from '../../stores/uiStore';
import { sanitizeReturnTo } from './ssoNavigation';

/**
 * One exchange per code, module-scoped. A `useRef` guard survives a
 * re-render but NOT a StrictMode remount (mount → unmount → mount), so
 * a ref alone would either replay the single-use exchange or leave the
 * second mount stranded on the spinner. Instead every mount subscribes
 * to the same cached promise: the first mount fires the POST, the
 * remount just awaits it.
 */
const exchangePromises = new Map<string, Promise<void>>();

export default function SsoCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const completeSso = useAuthStore((s) => s.completeSso);
  const theme = useUiStore((s) => s.resolvedTheme);
  const [error, setError] = useState<string | null>(null);

  const code = searchParams.get('code');
  const returnTo = sanitizeReturnTo(searchParams.get('returnTo'));

  useEffect(() => {
    if (!code) {
      setError('This sign-on link is missing its code. Start again from the login page.');
      return;
    }

    let exchange = exchangePromises.get(code);
    if (!exchange) {
      exchange = completeSso(code);
      exchangePromises.set(code, exchange);
    }

    let cancelled = false;
    exchange
      .then(() => {
        if (!cancelled) navigate(returnTo, { replace: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data
                ?.message
            : null;
        setError(message || 'Single sign-on failed — the sign-on code may have expired.');
      });
    return () => {
      cancelled = true;
    };
    // Intentionally mount-only: the code arrives with the initial URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-default-50">
      <div className="w-full max-w-sm space-y-6 p-6">
        <div className="flex flex-col items-center space-y-3">
          <Logo variant="stacked" size={140} onDark={theme === 'dark'} />
        </div>

        {error ? (
          <div className="space-y-4">
            <Alert status="danger">{error}</Alert>
            <p className="text-center text-sm">
              <Link
                to="/login"
                className="text-primary font-medium hover:underline"
              >
                Back to sign in
              </Link>
            </p>
          </div>
        ) : (
          <div
            className="flex flex-col items-center space-y-3"
            role="status"
            aria-label="Completing sign-on"
          >
            <Spinner />
            <p className="text-default-500 text-sm">Completing sign-on…</p>
          </div>
        )}
      </div>
    </div>
  );
}
